import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/20/solid";
import React, { Fragment, useEffect, useState } from "react";
import { LuImagePlus } from "react-icons/lu";
import { errorMsg, success } from "../../../utils/notifications";
import { baseURL } from "../../../api/api";
import {
  addCompany,
  getCompanyById,
  updateCompany,
} from "../../../api/companies.api";
import { MdRemoveRedEye } from "react-icons/md";
import { FaEyeSlash } from "react-icons/fa";

const initialData = {
  firstName: "",
  lastName: "",
  phone: "",
  email: "",
  password: "",
  profileImage: "",
  companyLogo: "",
  companyName: "",
  companyEmail: "",
  companyAddress: "",
  companyPhone: "",
  companyDescription: "",
};

const AddCompany = ({ open, setOpen, fetchCompanies, editCompanyId }) => {
  const [addCompanyData, setAddCompanyData] = useState(initialData);
  const [selectedProfileImage, setSelectedProfileImage] = useState(null);
  const [selectedCompanyLogo, setSelectedCompanyLogo] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  useEffect(() => {
    if (editCompanyId) {
      getCompanyById(editCompanyId)
        .then((res) => {
          console.log("get by id", res?.data?.company);
          setAddCompanyData({
            profileImage: res?.data?.company?.userId?.profileImage,
            firstName: res?.data?.company?.userId?.firstName,
            lastName: res?.data?.company?.userId?.lastName,
            phone: res?.data?.company?.userId?.phone,
            email: res?.data?.company?.userId?.email,
            password: res?.data?.company?.userId?.password,
            companyLogo: res?.data?.company?.logo,
            companyName: res?.data?.company?.name,
            companyEmail: res?.data?.company?.email,
            companyAddress: res?.data?.company?.address,
            companyPhone: res?.data?.company?.phone,
            companyDescription: res?.data?.company?.description,
          });
        })
        .catch((err) => {
          errorMsg("get By Id", err?.response?.data?.message);
          console.log("Error is", err);
        });
    } else {
      setAddCompanyData(initialData);
      setSelectedProfileImage(null);
      setSelectedCompanyLogo(null);
    }
  }, [editCompanyId]);

  const handleTogglePassword = (fieldName) => {
    if (fieldName === "password") {
      setShowPassword(!showPassword);
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    const type = event.target.getAttribute("id");
    if (type === "profileImage") {
      setSelectedProfileImage(file);
    } else if (type === "companyLogo") {
      setSelectedCompanyLogo(file);
    }
    console.log(`${type} is`, file);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    let newValue = value;

    if (name === "phone" || name === "companyPhone") {
      // Allow only digits and "+" at start
      newValue = value.replace(/[^\d+]/g, ""); // remove everything except + and digits

      // If "+" exists but not at the start, remove it
      if (newValue.includes("+") && newValue.indexOf("+") !== 0) {
        newValue = newValue.replace(/\+/g, "");
      }

      // If more than one "+" is entered, keep only the first one
      if ((newValue.match(/\+/g) || []).length > 1) {
        newValue = "+" + newValue.replace(/\+/g, "");
      }
    }

    setAddCompanyData({
      ...addCompanyData,
      [name]: newValue,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    const formData = new FormData();
    formData.append("companyLogo", selectedCompanyLogo);
    formData.append("profileImage", selectedProfileImage);
    formData.append("firstName", addCompanyData.firstName);
    formData.append("lastName", addCompanyData.lastName);
    formData.append("phone", addCompanyData.phone);
    formData.append("email", addCompanyData.email);
    formData.append("password", addCompanyData.password);
    formData.append("companyName", addCompanyData.companyName);
    formData.append("companyEmail", addCompanyData.companyEmail);
    formData.append("companyAddress", addCompanyData.companyAddress);
    formData.append("companyPhone", addCompanyData.companyPhone);
    formData.append("companyDescription", addCompanyData.companyDescription);

    try {
      if (editCompanyId) {
        const res = await updateCompany(editCompanyId, formData);
        console.log("updating data", res?.data);
        success("Successful", "Company Updated Successfully");
      } else {
        const res = await addCompany(formData);
        console.log("new", res?.data);
        setAddCompanyData(initialData);
        setSelectedCompanyLogo(null);
        setSelectedProfileImage(null);
        success("Successful", "Company Added Successfully");
      }
      fetchCompanies();
      setOpen(false);
    } catch (err) {
      errorMsg("Oops", err?.response?.data?.message);
      console.log("Error is", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="z-50" onClose={setOpen}>
        <Transition.Child
          enter="ease-out duration-500"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-300"
          leaveFrom="opacity-100 transform scale-100"
          leaveTo="opacity-0 transform scale-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-500"
              enterFrom="opacity-0 transform scale-0"
              enterTo="opacity-100 transform scale-100"
              leave="ease-in duration-300"
              leaveFrom="opacity-100 transform scale-100"
              leaveTo="opacity-0 transform scale-0"
            >
              <Dialog.Panel className="mt-12 lg:ml-60 md:ml-60 ml-0 z-50 lg:w-1/2 md:w-1/2 w-4/5 fixed mx-auto rounded-lg bg-white pt-5 pb-4 text-left shadow-xl transition-all h-auto">
                <div className="absolute top-0 right-0 pt-4 pr-4 sm:block">
                  <button
                    type="button"
                    className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                    onClick={() => setOpen(false)}
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>
                <div className="">
                  <div className=" sm:text-left">
                    <Dialog.Title
                      as="h3"
                      className="px-3 pb-5 text-xl font-medium border-b-2 border-gray-200 leading-6 text-gray-700"
                    >
                      {editCompanyId ? "Update Company" : "Add Company"}
                    </Dialog.Title>
                    <div className="">
                      <form onSubmit={handleSubmit} method="POST">
                        <div className="h-[30rem] overflow-y-scroll">
                          <div className="px-7 py-4">
                            <div className="flex items-center justify-between">
                              <div>
                                <h2 className="text-lg font-semibold text-gray-300 ">
                                  User Information
                                </h2>
                              </div>
                            </div>
                            <div>
                              <div className="py-0.5 md:py-1.5 lg:py-1.5 items-center grid grid-cols-1 sm:grid-cols-12 lg:grid-cols-12 md:grid-cols-12">
                                <div className="lg:col-span-5 md:col-span-5 sm:col-span-12 ">
                                  <label
                                    htmlFor="profileImage"
                                    className="block text-base font-semibold leading-6 text-gray-800"
                                  >
                                    Profile Image
                                  </label>
                                </div>
                                <div className="text-left lg:col-span-7 md:col-span-7 sm:col-span-12">
                                  <div className="w-1/3 mt-2 ">
                                    <label
                                      htmlFor="profileImage"
                                      className="mx-auto bg-gray-100 rounded-full h-28 w-28 inset-0 flex items-center justify-center cursor-pointer"
                                    >
                                      {selectedProfileImage ? (
                                        <img
                                          src={URL.createObjectURL(
                                            selectedProfileImage
                                          )}
                                          alt="Selected"
                                          className="rounded-full h-full w-full object-cover"
                                        />
                                      ) : editCompanyId &&
                                        addCompanyData?.profileImage ? (
                                        <img
                                          src={`${baseURL}/s3/images/${addCompanyData?.profileImage}`}
                                          alt="Company profileImage"
                                          className="rounded-full h-full w-full object-cover"
                                        />
                                      ) : (
                                        <LuImagePlus className="text-gray-400 h-14 w-14 p-2" />
                                      )}
                                    </label>
                                    <input
                                      type="file"
                                      name="profileImage"
                                      id="profileImage"
                                      className="hidden"
                                      onChange={handleFileChange}
                                    />
                                  </div>
                                </div>
                              </div>
                              <div className="py-0.5 md:py-1.5 lg:py-1.5 items-center grid grid-cols-1 sm:grid-cols-12 lg:grid-cols-12 md:grid-cols-12">
                                <div className="lg:col-span-5 md:col-span-5 sm:col-span-12 ">
                                  <label
                                    htmlFor="firstName"
                                    className="block text-base font-semibold leading-6 text-gray-800"
                                  >
                                    First Name{" "}
                                    <span className="text-red-500 ">*</span>
                                  </label>
                                </div>
                                <div className="lg:col-span-7 md:col-span-7 sm:col-span-12 ">
                                  <div className="mt-2">
                                    <input
                                      type="text"
                                      name="firstName"
                                      placeholder="Enter First name"
                                      value={addCompanyData?.firstName}
                                      onChange={handleChange}
                                      id="firstName"
                                      className="text-sm font-medium bg-white bg-opacity-80 px-3 block w-full rounded-md outline-0 py-2 text-gray-600 placeholder:text-gray-300 border-2 border-gray-200 border-opacity-70 focus:border-2 focus:border-yellowColor sm:leading-6 focus:ring-transparent"
                                      required
                                    />
                                  </div>
                                </div>
                              </div>
                              <div className="py-0.5 md:py-1.5 lg:py-1.5 items-center grid grid-cols-1 sm:grid-cols-12 lg:grid-cols-12 md:grid-cols-12">
                                <div className="lg:col-span-5 md:col-span-5 sm:col-span-12 ">
                                  <label
                                    htmlFor="lastName"
                                    className="block text-base font-semibold leading-6 text-gray-800"
                                  >
                                    Last Name{" "}
                                    <span className="text-red-500 ">*</span>
                                  </label>
                                </div>
                                <div className="lg:col-span-7 md:col-span-7 sm:col-span-12 ">
                                  <div className="mt-2">
                                    <input
                                      type="text"
                                      name="lastName"
                                      placeholder="Enter Last name"
                                      value={addCompanyData?.lastName}
                                      onChange={handleChange}
                                      id="lastName"
                                      className="text-sm font-medium bg-white bg-opacity-80 px-3 block w-full rounded-md outline-0 py-2 text-gray-600 placeholder:text-gray-300 border-2 border-gray-200 border-opacity-70 focus:border-2 focus:border-yellowColor sm:leading-6 focus:ring-transparent"
                                      required
                                    />
                                  </div>
                                </div>
                              </div>
                              <div className="py-0.5 md:py-1.5 lg:py-1.5 items-center grid grid-cols-1 sm:grid-cols-12 lg:grid-cols-12 md:grid-cols-12">
                                <div className="lg:col-span-5 md:col-span-5 sm:col-span-12 ">
                                  <label
                                    htmlFor="email"
                                    className="block text-base font-semibold leading-6 text-gray-800"
                                  >
                                    Email{" "}
                                    <span className="text-red-500 ">*</span>
                                  </label>
                                </div>
                                <div className="lg:col-span-7 md:col-span-7 sm:col-span-12 ">
                                  <div className="mt-2">
                                    <input
                                      type="email"
                                      name="email"
                                      placeholder="Enter Email"
                                      value={addCompanyData?.email}
                                      onChange={handleChange}
                                      id="email"
                                      className="text-sm font-medium bg-white bg-opacity-80 px-3 block w-full rounded-md outline-0 py-2 text-gray-600 placeholder:text-gray-300 border-2 border-gray-200 border-opacity-70 focus:border-2 focus:border-yellowColor sm:leading-6 focus:ring-transparent"
                                      required
                                    />
                                  </div>
                                </div>
                              </div>
                              {!editCompanyId && (
                                <div className="py-0.5 md:py-1.5 lg:py-1.5 items-center grid grid-cols-1 sm:grid-cols-12 lg:grid-cols-12 md:grid-cols-12">
                                  <div className="lg:col-span-5 md:col-span-5 sm:col-span-12 ">
                                    <label
                                      htmlFor="password"
                                      className="block text-base font-semibold leading-6 text-gray-800"
                                    >
                                      Password{" "}
                                      <span className="text-red-500 ">*</span>
                                    </label>
                                  </div>
                                  <div className="lg:col-span-7 md:col-span-7 sm:col-span-12 ">
                                    <div className="mt-2 relative">
                                      <input
                                        type={
                                          showPassword ? "text" : "password"
                                        }
                                        name="password"
                                        placeholder="Password"
                                        value={addCompanyData.password}
                                        onChange={handleChange}
                                        id="password"
                                        className="text-base font-medium bg-neutral-100 bg-opacity-80 px-3 pr-10 block w-full rounded-md border-0 outline-0 py-2.5 text-gray-600 placeholder:text-gray-400 border-2 border-transparent focus:border-2 focus:border-yellowColor sm:leading-6"
                                        required
                                      />
                                      {/* Eye icon */}
                                      <button
                                        className="absolute inset-y-0 right-0 flex items-center pr-3"
                                        onClick={() =>
                                          handleTogglePassword("password")
                                        }
                                        type="button"
                                      >
                                        {showPassword ? (
                                          <FaEyeSlash className="cursor-pointer text-gray-400 text-2xl" />
                                        ) : (
                                          <MdRemoveRedEye className="cursor-pointer text-gray-400 text-2xl" />
                                        )}
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              )}
                              <div className="py-0.5 md:py-1.5 lg:py-1.5 items-center grid grid-cols-1 sm:grid-cols-12 lg:grid-cols-12 md:grid-cols-12">
                                <div className="lg:col-span-5 md:col-span-5 sm:col-span-12 ">
                                  <label
                                    htmlFor="phone"
                                    className="block text-base font-semibold leading-6 text-gray-800"
                                  >
                                    Phone{" "}
                                    <span className="text-red-500 ">*</span>
                                  </label>
                                </div>
                                <div className="lg:col-span-7 md:col-span-7 sm:col-span-12 ">
                                  <div className="mt-2">
                                    <input
                                      type="text"
                                      name="phone"
                                      placeholder="Enter phone"
                                      value={addCompanyData?.phone}
                                      onChange={handleChange}
                                      id="phone"
                                      className="text-sm font-medium bg-white bg-opacity-80 px-3 block w-full rounded-md outline-0 py-2 text-gray-600 placeholder:text-gray-300 border-2 border-gray-200 border-opacity-70 focus:border-2 focus:border-yellowColor sm:leading-6 focus:ring-transparent"
                                      required
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="px-7 py-4">
                            <div className="flex items-center justify-between">
                              <div>
                                <h2 className="text-lg font-semibold text-gray-300 ">
                                  Company Information
                                </h2>
                              </div>
                            </div>
                            <div>
                              <div className="py-0.5 md:py-1.5 lg:py-1.5 items-center grid grid-cols-1 sm:grid-cols-12 lg:grid-cols-12 md:grid-cols-12">
                                <div className="lg:col-span-5 md:col-span-5 sm:col-span-12 ">
                                  <label
                                    htmlFor="companyLogo"
                                    className="block text-base font-semibold leading-6 text-gray-800"
                                  >
                                    Company Logo
                                  </label>
                                </div>
                                <div className="text-left lg:col-span-7 md:col-span-7 sm:col-span-12">
                                  <div className="w-1/3 mt-2 ">
                                    <label
                                      htmlFor="companyLogo"
                                      className="mx-auto bg-gray-100 rounded-full h-28 w-28 inset-0 flex items-center justify-center cursor-pointer"
                                    >
                                      {selectedCompanyLogo ? (
                                        <img
                                          src={URL.createObjectURL(
                                            selectedCompanyLogo
                                          )}
                                          alt="Selected"
                                          className="rounded-full h-full w-full object-cover"
                                        />
                                      ) : editCompanyId &&
                                        addCompanyData?.companyLogo ? (
                                        <img
                                          src={`${baseURL}/s3/images/${addCompanyData?.companyLogo}`}
                                          alt="Company Logo"
                                          className="rounded-full h-full w-full object-cover"
                                        />
                                      ) : (
                                        <LuImagePlus className="text-gray-400 h-14 w-14 p-2" />
                                      )}
                                    </label>
                                    <input
                                      type="file"
                                      name="companyLogo"
                                      id="companyLogo"
                                      className="hidden"
                                      onChange={handleFileChange}
                                    />
                                  </div>
                                </div>
                              </div>
                              <div className="py-0.5 md:py-1.5 lg:py-1.5 items-center grid grid-cols-1 sm:grid-cols-12 lg:grid-cols-12 md:grid-cols-12">
                                <div className="lg:col-span-5 md:col-span-5 sm:col-span-12 ">
                                  <label
                                    htmlFor="companyName"
                                    className="block text-base font-semibold leading-6 text-gray-800"
                                  >
                                    Name{" "}
                                    <span className="text-red-500 ">*</span>
                                  </label>
                                </div>
                                <div className="lg:col-span-7 md:col-span-7 sm:col-span-12 ">
                                  <div className="mt-2">
                                    <input
                                      type="text"
                                      name="companyName"
                                      placeholder="Enter Company name"
                                      value={addCompanyData?.companyName}
                                      onChange={handleChange}
                                      id="companyName"
                                      className="text-sm font-medium bg-white bg-opacity-80 px-3 block w-full rounded-md outline-0 py-2 text-gray-600 placeholder:text-gray-300 border-2 border-gray-200 border-opacity-70 focus:border-2 focus:border-yellowColor sm:leading-6 focus:ring-transparent"
                                      required
                                    />
                                  </div>
                                </div>
                              </div>
                              <div className="py-0.5 md:py-1.5 lg:py-1.5 items-center grid grid-cols-1 sm:grid-cols-12 lg:grid-cols-12 md:grid-cols-12">
                                <div className="lg:col-span-5 md:col-span-5 sm:col-span-12 ">
                                  <label
                                    htmlFor="companyEmail"
                                    className="block text-base font-semibold leading-6 text-gray-800"
                                  >
                                    Company Email{" "}
                                    <span className="text-red-500 ">*</span>
                                  </label>
                                </div>
                                <div className="lg:col-span-7 md:col-span-7 sm:col-span-12 ">
                                  <div className="mt-2">
                                    <input
                                      type="email"
                                      name="companyEmail"
                                      placeholder="Enter Company Email"
                                      value={addCompanyData?.companyEmail}
                                      onChange={handleChange}
                                      id="companyEmail"
                                      className="text-sm font-medium bg-white bg-opacity-80 px-3 block w-full rounded-md outline-0 py-2 text-gray-600 placeholder:text-gray-300 border-2 border-gray-200 border-opacity-70 focus:border-2 focus:border-yellowColor sm:leading-6 focus:ring-transparent"
                                      required
                                    />
                                  </div>
                                </div>
                              </div>
                              <div className="py-0.5 md:py-1.5 lg:py-1.5 items-center grid grid-cols-1 sm:grid-cols-12 lg:grid-cols-12 md:grid-cols-12">
                                <div className="lg:col-span-5 md:col-span-5 sm:col-span-12 ">
                                  <label
                                    htmlFor="companyAddress"
                                    className="block text-base font-semibold leading-6 text-gray-800"
                                  >
                                    Address{" "}
                                    <span className="text-red-500 ">*</span>
                                  </label>
                                </div>
                                <div className="lg:col-span-7 md:col-span-7 sm:col-span-12 ">
                                  <div className="mt-2">
                                    <input
                                      type="text"
                                      name="companyAddress"
                                      placeholder="Enter Company Address"
                                      value={addCompanyData?.companyAddress}
                                      onChange={handleChange}
                                      id="companyAddress"
                                      className="text-sm font-medium bg-white bg-opacity-80 px-3 block w-full rounded-md outline-0 py-2 text-gray-600 placeholder:text-gray-300 border-2 border-gray-200 border-opacity-70 focus:border-2 focus:border-yellowColor sm:leading-6 focus:ring-transparent"
                                      required
                                    />
                                  </div>
                                </div>
                              </div>
                              <div className="py-0.5 md:py-1.5 lg:py-1.5 items-center grid grid-cols-1 sm:grid-cols-12 lg:grid-cols-12 md:grid-cols-12">
                                <div className="lg:col-span-5 md:col-span-5 sm:col-span-12 ">
                                  <label
                                    htmlFor="companyPhone"
                                    className="block text-base font-semibold leading-6 text-gray-800"
                                  >
                                    Phone{" "}
                                    <span className="text-red-500 ">*</span>
                                  </label>
                                </div>
                                <div className="lg:col-span-7 md:col-span-7 sm:col-span-12 ">
                                  <div className="mt-2">
                                    <input
                                      type="text"
                                      name="companyPhone"
                                      placeholder="Enter Company phone"
                                      value={addCompanyData?.companyPhone}
                                      onChange={handleChange}
                                      id="companyPhone"
                                      className="text-sm font-medium bg-white bg-opacity-80 px-3 block w-full rounded-md outline-0 py-2 text-gray-600 placeholder:text-gray-300 border-2 border-gray-200 border-opacity-70 focus:border-2 focus:border-yellowColor sm:leading-6 focus:ring-transparent"
                                      required
                                    />
                                  </div>
                                </div>
                              </div>
                              <div className="py-0.5 md:py-1.5 lg:py-1.5 items-center grid grid-cols-1 sm:grid-cols-12 lg:grid-cols-12 md:grid-cols-12">
                                <div className="lg:col-span-5 md:col-span-5 sm:col-span-12 ">
                                  <label
                                    htmlFor="companyDescription"
                                    className="block text-base font-semibold leading-6 text-gray-800"
                                  >
                                    Description
                                    <span className="text-red-500 ">*</span>
                                  </label>
                                </div>
                                <div className="lg:col-span-7 md:col-span-7 sm:col-span-12">
                                  <div className="mt-2">
                                    <textarea
                                      name="companyDescription"
                                      placeholder="EnterCompany Description"
                                      value={addCompanyData?.companyDescription}
                                      onChange={handleChange}
                                      id="companyDescription"
                                      className="text-sm font-medium bg-white bg-opacity-80 px-3 block w-full rounded-md outline-0 py-2 text-gray-600 placeholder:text-gray-300 border-2 border-gray-200 border-opacity-70 focus:border-2 focus:border-yellowColor sm:leading-6 focus:ring-transparent"
                                      required
                                      rows="4"
                                    ></textarea>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="border-t-2 border-gray-200 px-7 pt-3 flex items-center justify-end">
                          <button
                            type="submit"
                            disabled={isSubmitting}
                            className={`flex justify-center rounded-md bg-yellowColor px-3 py-2.5 px-10 text-base font-semibold leading-6 text-dark shadow-sm hover:bg-opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-yellowColor ${
                              isSubmitting
                                ? "opacity-50 cursor-not-allowed"
                                : ""
                            }`}
                          >
                            {isSubmitting
                              ? "Processing..."
                              : editCompanyId
                              ? "Update"
                              : "Submit"}
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default AddCompany;
